/**
 * What this browser is allowed to remember about a signed-in account.
 *
 * The vault is offline-first, so a reload with no network must still open the
 * cached vault. But "no network" is not proof of identity: before this, any
 * reload while offline opened the last account's vault outright. The rule here
 * is that an offline launch replays a session this browser *did* validate, and
 * only for as long as that validation is good for — the session's own
 * expiry, or a bounded grace window when the server states none.
 */
export const LAST_ACCOUNT_KEY = "recallvault.lastAccount";
export const SESSION_VALID_UNTIL_KEY = "recallvault.sessionValidUntil";

/** How long a validated session keeps opening the vault offline. */
export const OFFLINE_GRACE_MS = 7 * 24 * 60 * 60 * 1_000;

export interface ValidatedSession {
  readonly accountId: string;
  readonly expiresAt?: string;
}

export function rememberValidatedSession(
  store: Storage | undefined,
  session: ValidatedSession,
  now: number = Date.now(),
): void {
  const expires = session.expiresAt ? Date.parse(session.expiresAt) : Number.NaN;
  const validUntil = Number.isFinite(expires) ? expires : now + OFFLINE_GRACE_MS;
  try {
    store?.setItem(LAST_ACCOUNT_KEY, session.accountId);
    store?.setItem(SESSION_VALID_UNTIL_KEY, new Date(validUntil).toISOString());
  } catch { /* private mode */ }
}

/**
 * The account an offline launch may open, or undefined when this browser has
 * no unexpired proof that anyone is signed in.
 */
export function cachedOfflineAccount(store: Storage | undefined, now: number = Date.now()): string | undefined {
  let account: string | null | undefined;
  let validUntil: string | null | undefined;
  try {
    account = store?.getItem(LAST_ACCOUNT_KEY);
    validUntil = store?.getItem(SESSION_VALID_UNTIL_KEY);
  } catch { /* private mode */ }
  if (!account || !validUntil) return undefined;
  const deadline = Date.parse(validUntil);
  if (!Number.isFinite(deadline) || deadline <= now) return undefined;
  return account;
}

/** A refusal removes offline permission while preserving queued account work. */
export function forgetSessionValidation(store: Storage | undefined): void {
  try {
    store?.removeItem(LAST_ACCOUNT_KEY);
    store?.removeItem(SESSION_VALID_UNTIL_KEY);
  } catch { /* private mode */ }
}

/**
 * Drops every trace of the account this browser held: the identifiers, the
 * offline permission, the selected note, queued alert intents, preferences.
 * Sign-out erases, it does not merely forget which account you were.
 */
export function forgetLocalAccount(store: Storage | undefined, ownerAccountId?: string): void {
  try {
    if (!store) return;
    const keys: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (!key?.startsWith("recallvault.")) continue;
      if (!ownerAccountId || key === LAST_ACCOUNT_KEY || key === SESSION_VALID_UNTIL_KEY
        || key.endsWith(`:${ownerAccountId}`)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch { /* private mode */ }
}
